import { z } from 'zod'

import {
  BrandedMap,
  CreateBrandedEnum,
  type ZodInfer,
} from '@mntn-dev/utility-types'

import { MntnCreditProgramIdSchema } from './mntn-credit-program-id.ts'

export const [
  PartnerProgramKinds,
  PartnerProgramKindSchema,
  PartnerProgramKindEnum,
  unbrandPartnerProgramKind,
] = CreateBrandedEnum(
  { brand: 'PartnerProgramKind' },
  'mntn_credits',
  'meta',
  'tiktok',
  'linkedin'
)

export type PartnerProgramKind = ZodInfer<typeof PartnerProgramKindSchema>

/**
 * Partner programs that agencies can be approved for.
 * Currently identical to PartnerProgramKindSchema; split out so the
 * two concepts can diverge independently if needed.
 */
export const AgencyApprovedProgramKindSchema = PartnerProgramKindSchema

export type AgencyApprovedProgramKind = PartnerProgramKind

export const isPartnerProgramKind = (
  value: string
): value is PartnerProgramKind => {
  return PartnerProgramKindSchema.safeParse(value).success
}

export function isPartnerProgramId(value: string): value is PartnerProgramKind {
  return PartnerProgramKindSchema.safeParse(value).success
}

export function assertIsPartnerProgramKind(
  value: string
): asserts value is PartnerProgramKind {
  if (!isPartnerProgramKind(value)) {
    throw new Error('Invalid partner program kind')
  }
}

export const isMNTNCreativeProgram = (value?: string): boolean => {
  return value === 'mntn_credits'
}

export const CreditProgramRequiresMembership = new BrandedMap<
  PartnerProgramKind,
  boolean
>({
  mntn_credits: true,
  meta: false,
  tiktok: false,
  linkedin: false,
})

export const OtherCreditProgramMembershipIdSchema = z
  .string()
  .min(1)
  .max(128)
  .brand<'other-credit-program-membership'>()

export const CreditProgramMembershipIdSchema = z.union([
  MntnCreditProgramIdSchema,
  OtherCreditProgramMembershipIdSchema,
])

export type CreditProgramMembershipId = ZodInfer<
  typeof CreditProgramMembershipIdSchema
>

export const CreditProgramMembershipId = (value: string) => {
  return CreditProgramMembershipIdSchema.parse(value)
}

export const MakerImplicitPartnerPrograms: PartnerProgramKind[] = [
  PartnerProgramKindEnum.linkedin,
]
